import './WeatherDashboard.scss';
import { AgWidgetContainer } from 'app/agriculture/components/AgWidgetContainer/AgWidgetContainer';
import { AgWidget } from 'app/agriculture/components/AgWidget/AgWidget';
import { useContext } from 'react';
import { WeatherDashboardContext } from 'app/agriculture/routes/Devices/DeviceDetails/context/WeatherDashboardContextProvider';
import { WeatherTable } from 'app/agriculture/routes/WeatherModal/components/WeatherTable';
import { TopBarWidget } from 'app/agriculture/routes/WeatherModal/components/TopBarWidget/TopBarWidget';
import { PressureIcon, RainIcon, TemperatureIcon, WindIcon, UvIcon } from 'app/agriculture/icons';
import { BarChart } from 'app/agriculture/charts/BarChart/BarChart';
import {
    getAggregateDataFieldRecord,
    useAggregateChartData,
} from 'app/agriculture/hooks/useAggregateChartData/useAggregateChartData';
import { useTimelineCursorTime } from 'app/agriculture/AgTimeline/context/TimelineContext';
import { LineChart } from 'app/agriculture/charts/LineChart/LineChart';
import { ChartContextProvider } from 'app/agriculture/charts/ChartContextProvider';
import { MultiLineChart } from 'app/agriculture/charts/LineChart/MultiLineChart';
import { TZDate } from '@date-fns/tz';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';

export function WeatherDashboard() {
    const { weatherStationId, dashboardViewConfig } = useContext(WeatherDashboardContext);
    const { timeZone } = useTimeZone();
    const cursorTime = useTimelineCursorTime();
    const now = new TZDate(new Date(), timeZone);
    const data = useAggregateChartData({
        snapshotType: 'Weather',
        interval: dashboardViewConfig.getNewInterval(cursorTime ?? now),
        deviceId: weatherStationId ?? '',
        intervalUnit: dashboardViewConfig.intervalUnit,
    });

    if (weatherStationId == null) {
        return <div>No registered weather stations</div>;
    }

    return (
        <ChartContextProvider
            intervalUnit={dashboardViewConfig.intervalUnit}
            dateFormat={dashboardViewConfig.chartDateFormat}
        >
            <div className={'WeatherDashboard'}>
                <TopBarWidget />
                <AgWidgetContainer className="Dashboards" gridColumns={12} gridRows={12}>
                    <AgWidget
                        disablePadding
                        title={dashboardViewConfig.tableTitle}
                        gridColumns={12}
                        gridRows={4}
                    >
                        <WeatherTable
                            dateHeader={dashboardViewConfig.dateTableHeader}
                            weatherStationId={weatherStationId}
                            intervalUnit={dashboardViewConfig.intervalUnit}
                            interval={dashboardViewConfig.getNewInterval(now)}
                            dateFormat={dashboardViewConfig.tableDateFormat}
                        />
                    </AgWidget>
                    <AgWidget
                        title="Rainfall"
                        gridColumns={6}
                        gridRows={8}
                        Icon={(props) => <RainIcon {...props} title="Rainfall" />}
                    >
                        <BarChart
                            data={getAggregateDataFieldRecord(data, 'rainfallSum')}
                            showCurrentValue={false}
                            displayUnit="mm"
                        />
                    </AgWidget>
                    <AgWidget
                        title="Temperature"
                        gridColumns={3}
                        gridRows={4}
                        Icon={(props) => <TemperatureIcon {...props} title="Temperature" />}
                    >
                        {dashboardViewConfig.intervalUnit === 'hour' ? (
                            <LineChart
                                data={getAggregateDataFieldRecord(data, 'temperatureAvg')}
                                showCurrentValue={false}
                                displayUnit="°C"
                            />
                        ) : (
                            <MultiLineChart
                                datasets={[
                                    {
                                        data: getAggregateDataFieldRecord(data, 'temperatureMax'),
                                        label: 'Max',
                                        colour: 'rgba(252, 100, 31, 1)',
                                    },
                                    {
                                        data: getAggregateDataFieldRecord(data, 'temperatureMin'),
                                        label: 'Min',
                                        colour: 'rgba(34, 195, 219, 1)',
                                    },
                                ]}
                                showCurrentValue={false}
                                displayUnit="°C"
                            />
                        )}
                    </AgWidget>
                    <AgWidget
                        title="Windspeed"
                        gridColumns={3}
                        gridRows={4}
                        Icon={(props) => <WindIcon {...props} title="Wind Speed" />}
                    >
                        <LineChart
                            data={getAggregateDataFieldRecord(data, 'windSpeedAvg')}
                            showCurrentValue={false}
                            displayUnit="km/h"
                            chartOptionsOverride={{
                                scales: {
                                    y: {
                                        suggestedMin: 0,
                                        suggestedMax: 10,
                                    },
                                },
                            }}
                        />
                    </AgWidget>

                    <AgWidget
                        gridColumns={3}
                        gridRows={4}
                        title="Pressure"
                        Icon={(props) => <PressureIcon {...props} title="Pressure" />}
                    >
                        <LineChart
                            data={getAggregateDataFieldRecord(data, 'absPressureAvg')}
                            showCurrentValue={false}
                            displayUnit="hPa"
                            chartOptionsOverride={{
                                scales: {
                                    y: {
                                        suggestedMin: 950,
                                        suggestedMax: 1050,
                                    },
                                },
                            }}
                        />
                    </AgWidget>
                    <AgWidget
                        title="UV Index"
                        gridColumns={3}
                        gridRows={4}
                        Icon={(props) => <UvIcon {...props} title="UV Index" />}
                    >
                        <LineChart
                            data={getAggregateDataFieldRecord(data, 'uvIndexMax')}
                            showCurrentValue={false}
                            displayUnit=""
                            chartOptionsOverride={{
                                scales: {
                                    y: {
                                        suggestedMin: 0,
                                        suggestedMax: 10,
                                    },
                                },
                            }}
                        />
                    </AgWidget>
                </AgWidgetContainer>
            </div>
        </ChartContextProvider>
    );
}
